import React, { ReactElement, useEffect, useState } from 'react';

import { Box, DialogContent, Typography, Grid2, Button } from '@mui/material';
import { DemandOrderDto, OrderDto, OrderStatus, ReturnOrderDto } from '../../../api/ClientApi';
import {
    clearBareGoods,
    clearOrder,
    createNewEmptyOrder,
    createNewOrder,
    importOrder,
    saveOrder,
    setBareGoodsEntities,
    setDestinationSite,
    setOriginSite,
} from '../../../redux/actions/orderActions';
import { useAppDispatch } from '../../../hooks/hooks';
import { useSitesWithGoods } from '../../../hooks/useSitesWithGoods';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { SaveEntityStatus } from '../../../redux/reducers/common';
import { CompleteOrderType } from '../../../common/Types';
import { getSuppliers } from '../../../redux/selectors/clientsSelector';
import { importSites } from '../../../redux/actions/siteActions';
import { importClients } from '../../../redux/actions/clientActions';
import { importAllGoods } from '../../../redux/actions/goodsActions';
import OrderFormGoods from './OrderFormGoods';
import { importArticles } from '../../../redux/actions/articleActions';
import { filterUniqueBareGoodsByArticle } from '../../../redux/actions/helper';
import { WarehouseClient } from '../../../api/Extensions';
import SiteAutocomplete from '../../../common/components/autocomplete/SiteAutocomplete';
import { BareGoodsEntity } from '../../../redux/reducers/goodsToOrderFormReducer';
import { convertGoodsToBareGoodsEntities } from './OrderFormHelper';

import { FormActions, FormDialogTitle } from '../../../common/components/form/FormPrimitives';

import { ResponsiveDialog } from '../../../common/components/mobile/MobilePrimitives';

type OrderFormProps = {
    closeForm(): void;
    open: boolean;
    orderId?: number;
    demandOrderId?: number;
    returnOrderId?: number;
    originOrderType?: CompleteOrderType;
};

export default function OrderForm(props: OrderFormProps): ReactElement {
    const { open, closeForm, orderId, demandOrderId, returnOrderId, originOrderType } = props;

    const order = useSelector((state: RootState) => state.orderForm.order);
    const saveStatus = useSelector((state: RootState) => state.orderForm.saveStatus);
    const bareGoods = useSelector((state: RootState) => state.goodsToOrderForm.bareGoodsEntities);

    const suppliers = useSelector(getSuppliers);
    const sites = useSitesWithGoods();
    const goods = useSelector((state: RootState) => state.goods.goods);
    const articles = useSelector((state: RootState) => state.articles.articles);

    const [localDemandOrder, setLocalDemandOrder] = useState<DemandOrderDto | undefined>(undefined);
    const [localReturnOrder, setLocalReturnOrder] = useState<ReturnOrderDto | undefined>(undefined);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (open) {
            if (!!demandOrderId && originOrderType === CompleteOrderType.Demand) {
                WarehouseClient.getDemandOrder(demandOrderId).then((res) => {
                    const newOrder = new OrderDto({
                        orderStatus: OrderStatus.Idle,
                        demandOrderId: demandOrderId,
                        destinationSiteId: res.destinationSiteId,
                        destinationSite: res.destinationSite,
                    });
                    dispatch(createNewOrder(newOrder));
                    setLocalDemandOrder(res);
                });
            }
        }
    }, [dispatch, open, demandOrderId, originOrderType]);

    useEffect(() => {
        if (open) {
            if (!!returnOrderId && originOrderType === CompleteOrderType.GeneralReturn) {
                WarehouseClient.getReturnOrder(returnOrderId).then((res) => {
                    const newOrder = new OrderDto({
                        orderStatus: OrderStatus.Idle,
                        returnOrderId: returnOrderId,
                        originSiteId: res.originSiteId,
                        originSite: res.originSite,
                    });
                    dispatch(createNewOrder(newOrder));
                    setLocalReturnOrder(res);
                });
            }
        }
    }, [dispatch, open, returnOrderId, originOrderType]);

    useEffect(() => {
        if (open) {
            if (!!orderId && orderId > 0) {
                dispatch(importOrder(orderId));
            } else {
                if (
                    !order &&
                    originOrderType !== CompleteOrderType.Demand &&
                    originOrderType !== CompleteOrderType.GeneralReturn
                ) {
                    dispatch(createNewEmptyOrder());
                }
            }
        }
    }, [dispatch, orderId, open, originOrderType]);

    useEffect(() => {
        if (open) {
            if (!sites) {
                dispatch(importSites());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!suppliers) {
                dispatch(importClients());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!goods) {
                dispatch(importAllGoods());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (open) {
            if (!articles) {
                dispatch(importArticles());
            }
        }
    }, [dispatch, open]);

    useEffect(() => {
        if (saveStatus === SaveEntityStatus.Saved) {
            dispatch(importAllGoods());
            handleClose();
        }
    }, [dispatch, saveStatus]);

    const handleSelectOriginSite = (siteId?: number) => {
        const site = sites?.find((s) => s.id === siteId);

        dispatch(setOriginSite(site));
        dispatch(clearBareGoods());
    };

    const handleSelectDestinationSite = (siteId?: number) => {
        const site = sites?.find((s) => s.id === siteId);

        dispatch(setDestinationSite(site));
    };

    const handleSave = () => {
        dispatch(saveOrder());
    };

    const handleClose = () => {
        dispatch(clearOrder());
        dispatch(clearBareGoods());
        setLocalDemandOrder(undefined);
        setLocalReturnOrder(undefined);
        closeForm();
    };

    const addAllDemandedGoodsFromSite = () => {
        const siteGoods = order?.originSite?.goods;
        const orderedGoods = localDemandOrder?.orderGoodsItems;

        const orderedBareGoods: BareGoodsEntity[] = [];

        const siteBareGoods = convertGoodsToBareGoodsEntities(siteGoods);

        orderedGoods?.map((og) => {
            const siteGood = siteBareGoods?.find((sg) => sg.article?.id === og.articleId);

            if (!!siteGood) {
                const index =
                    orderedBareGoods.length === 0 ? 1 : orderedBareGoods[orderedBareGoods.length - 1].index + 1;

                const alreadyOrderedItems =
                    localDemandOrder?.orders
                        ?.filter(
                            (o) => o?.orderStatus !== OrderStatus.Cancelled && o?.orderStatus !== OrderStatus.Returned,
                        )
                        .map((o) => o.orderGoodsItems ?? [])
                        .flat() ?? [];

                const alreadyOrderedQuantity =
                    alreadyOrderedItems
                        ?.filter((aoi) => aoi.articleId === siteGood.article?.id)
                        .reduce((acc, val) => {
                            return (acc += val.quantity ?? 0);
                        }, 0) ?? undefined;

                const orderedGoodsQuantity = !!og.quantity ? og.quantity - alreadyOrderedQuantity : 0;

                const quantity = !!orderedGoodsQuantity
                    ? !!siteGood.availableQuantity
                        ? orderedGoodsQuantity <= siteGood.availableQuantity
                            ? orderedGoodsQuantity
                            : siteGood.availableQuantity
                        : 0
                    : 0;

                const newBareGoods: BareGoodsEntity = {
                    index: index,
                    availableQuantity: siteGood.availableQuantity ?? 0,
                    goods: siteGood.goods,
                    article: siteGood.article,
                    orderedQuantity: quantity,
                };

                if (orderedGoodsQuantity > 0) {
                    orderedBareGoods.push(newBareGoods);
                }
            }
        });

        dispatch(setBareGoodsEntities(orderedBareGoods));
    };

    const addAllReturnGoodsFromSite = () => {
        const originSite = sites?.find((s) => s.id === order?.originSiteId);

        const siteGoods = originSite?.goods;
        const orderedGoods = localReturnOrder?.orderGoodsItems;

        const orderedBareGoods: BareGoodsEntity[] = [];

        const siteBareGoods = convertGoodsToBareGoodsEntities(siteGoods);

        orderedGoods?.map((og) => {
            const siteGood = siteBareGoods?.find((sg) => sg.article?.id === og.articleId);

            if (!!siteGood) {
                const index =
                    orderedBareGoods.length === 0 ? 1 : orderedBareGoods[orderedBareGoods.length - 1].index + 1;

                const alreadyOrderedItems =
                    localReturnOrder?.orders
                        ?.filter(
                            (o) => o.orderStatus !== OrderStatus.Cancelled && o.orderStatus !== OrderStatus.Returned,
                        )
                        .map((o) => o.orderGoodsItems ?? [])
                        .flat() ?? [];

                const alreadyOrderedQuantity =
                    alreadyOrderedItems
                        ?.filter((aoi) => aoi.articleId === siteGood.article?.id)
                        .reduce((acc, val) => {
                            return (acc += val.quantity ?? 0);
                        }, 0) ?? undefined;

                const orderedGoodsQuantity = !!og.quantity ? og.quantity - alreadyOrderedQuantity : 0;

                const quantity = !!orderedGoodsQuantity
                    ? !!siteGood.availableQuantity
                        ? orderedGoodsQuantity <= siteGood.availableQuantity
                            ? orderedGoodsQuantity
                            : siteGood.availableQuantity
                        : 0
                    : 0;

                const newBareGoods: BareGoodsEntity = {
                    index: index,
                    availableQuantity: siteGood.availableQuantity ?? 0,
                    goods: siteGood.goods,
                    article: siteGood.article,
                    orderedQuantity: quantity,
                };

                if (orderedGoodsQuantity > 0) {
                    orderedBareGoods.push(newBareGoods);
                }
            }
        });

        dispatch(setBareGoodsEntities(orderedBareGoods));
    };

    const isAvailableQuantityEnough =
        filterUniqueBareGoodsByArticle(bareGoods ?? []).filter((bg) => bg.orderedQuantity > bg.availableQuantity)
            .length <= 0;

    const isOrderDataValid =
        !!order &&
        !!order.orderStatus &&
        !(
            (!!order.originSiteId && order.originSiteId > 0 && !!order.supplierId && order.supplierId > 0) ||
            ((!order.originSiteId || order.originSiteId <= 0) && (!order.supplierId || order.supplierId <= 0))
        ) &&
        !!order.destinationSiteId &&
        order.destinationSiteId > 0 &&
        !!bareGoods &&
        bareGoods.length > 0 &&
        bareGoods.filter((bg) => !bg.article || bg.orderedQuantity <= 0).length === 0;

    return (
        <ResponsiveDialog scroll="body" maxWidth="md" open={open} onClose={handleClose}>
            <FormDialogTitle>
                Nalog za otpremanje {!!order?.demandOrderId && order.demandOrderId > 0 && ' (iz trebovanja)'}
                {!!order?.returnOrderId && order.returnOrderId > 0 && ' (iz povratnice)'} -{' '}
                {!orderId ? 'kreiranje' : 'izmena'}{' '}
            </FormDialogTitle>
            <DialogContent dividers sx={{ px: { xs: 2, sm: 5 }, pt: { xs: 3, sm: 5 } }}>
                <Grid2 container spacing={2} style={{ maxWidth: '800px', minWidth: '600px', margin: 'auto' }}>
                    {(!!localDemandOrder || !!localReturnOrder) && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography variant="h6">Zahtevana roba</Typography>
                        </Grid2>
                    )}
                    {localDemandOrder &&
                        localDemandOrder.orderGoodsItems &&
                        localDemandOrder.orderGoodsItems.map((ogi) => {
                            const quantityLeft =
                                (ogi.quantity ?? 0) -
                                (localDemandOrder?.orders
                                    ?.filter(
                                        (o) =>
                                            o.orderStatus !== OrderStatus.Cancelled &&
                                            o.orderStatus !== OrderStatus.Returned,
                                    )
                                    .reduce((acc, val) => {
                                        return (acc +=
                                            val?.orderGoodsItems
                                                ?.filter((og) => og.articleId === ogi.articleId)
                                                .reduce((a, v) => {
                                                    return (a += v.quantity ?? 0);
                                                }, 0) ?? 0);
                                    }, 0) ?? 0);
                            return (
                                <Grid2 key={Math.random()} size={{ xs: 12 }}>
                                    <Box>
                                        <Typography variant="body1" minWidth={'100%'} sx={{ display: 'flex' }}>
                                            {(ogi.article?.manufacturer ?? '') + ', ' + (ogi.article?.title ?? '')}
                                            <span style={{ flexGrow: 1 }} />
                                            <span style={{ fontWeight: 'bold' }}>{quantityLeft}</span>
                                        </Typography>
                                    </Box>
                                </Grid2>
                            );
                        })}
                    {localReturnOrder &&
                        localReturnOrder.orderGoodsItems &&
                        localReturnOrder.orderGoodsItems.map((ogi) => {
                            const quantityLeft =
                                (ogi.quantity ?? 0) -
                                (localReturnOrder?.orders
                                    ?.filter(
                                        (o) =>
                                            o.orderStatus !== OrderStatus.Cancelled &&
                                            o.orderStatus !== OrderStatus.Returned,
                                    )
                                    .reduce((acc, val) => {
                                        return (acc +=
                                            val?.orderGoodsItems
                                                ?.filter((og) => og.articleId === ogi.articleId)
                                                .reduce((a, v) => {
                                                    return (a += v.quantity ?? 0);
                                                }, 0) ?? 0);
                                    }, 0) ?? 0);
                            return (
                                <Grid2 key={Math.random()} size={{ xs: 12 }}>
                                    <Box>
                                        <Typography variant="body1" minWidth={'100%'} sx={{ display: 'flex' }}>
                                            {(ogi.article?.manufacturer ?? '') + ', ' + (ogi.article?.title ?? '')}:
                                            <span style={{ flexGrow: 1 }} />
                                            <span style={{ fontWeight: 'bold' }}>{quantityLeft}</span>
                                        </Typography>
                                    </Box>
                                </Grid2>
                            );
                        })}
                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        {originOrderType === CompleteOrderType.GeneralReturn ? (
                            <Box>
                                <Typography variant="body1">
                                    Izvor - skladište:{' '}
                                    <span style={{ fontWeight: 'bold' }}>
                                        {localReturnOrder?.originSite?.name ?? '/'}
                                    </span>
                                </Typography>
                            </Box>
                        ) : !!orderId ? (
                            <Box>
                                <Typography variant="body1">
                                    Izvor - skladište:{' '}
                                    <span style={{ fontWeight: 'bold' }}>{order?.originSite?.name ?? '/'}</span>
                                </Typography>
                            </Box>
                        ) : (
                            <SiteAutocomplete
                                isRequired
                                selectSite={handleSelectOriginSite}
                                sites={sites?.filter((s) => s.id !== order?.destinationSiteId) ?? []}
                                selectedSiteId={order?.originSiteId}
                                title="Izvor - skladište"
                            />
                        )}
                    </Grid2>

                    <Grid2 size={{ xs: 12, sm: 6 }}>
                        {originOrderType === CompleteOrderType.Demand ? (
                            <Box>
                                <Typography variant="body1">
                                    Odredište - skladište:{' '}
                                    <span style={{ fontWeight: 'bold' }}>
                                        {localDemandOrder?.destinationSite?.name ?? '/'}
                                    </span>
                                </Typography>
                            </Box>
                        ) : !!orderId ? (
                            <Box>
                                <Typography variant="body1">
                                    Odredište - skladište:{' '}
                                    <span style={{ fontWeight: 'bold' }}>{order?.destinationSite?.name ?? '/'}</span>
                                </Typography>
                            </Box>
                        ) : (
                            <SiteAutocomplete
                                isRequired
                                selectSite={handleSelectDestinationSite}
                                sites={sites?.filter((s) => s.id !== order?.originSiteId) ?? []}
                                selectedSiteId={order?.destinationSiteId}
                                title="Odredište - skladište"
                            />
                        )}
                    </Grid2>
                    {localDemandOrder &&
                        localDemandOrder.orderGoodsItems &&
                        !!order?.originSiteId &&
                        order.originSiteId > 0 && (
                            <Grid2 size={{ xs: 12 }}>
                                <Button variant="outlined" onClick={addAllDemandedGoodsFromSite}>
                                    Izaberi svu robu sa skladišta
                                </Button>
                            </Grid2>
                        )}
                    {localReturnOrder &&
                        localReturnOrder.orderGoodsItems &&
                        !!localReturnOrder.originSiteId &&
                        localReturnOrder.originSiteId > 0 && (
                            <Grid2 size={{ xs: 12 }}>
                                <Button variant="outlined" onClick={addAllReturnGoodsFromSite}>
                                    Izaberi svu robu sa skladišta
                                </Button>
                            </Grid2>
                        )}
                    <Grid2 size={{ xs: 12 }}>
                        <OrderFormGoods
                            completeOrderType={CompleteOrderType.Transfer}
                            isOriginChosen={
                                originOrderType === CompleteOrderType.GeneralReturn ||
                                (!!order?.originSiteId && order.originSiteId > 0)
                            }
                            isReceipt={false}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 0, sm: 12 }} style={{ height: 15, margin: 0, padding: 0 }} />
                    {!isOrderDataValid && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*unesite sva obavezna polja</Typography>
                        </Grid2>
                    )}
                    {!isAvailableQuantityEnough && (
                        <Grid2 size={{ xs: 12 }}>
                            <Typography color="error">*nema dovoljno robe za zahtevani nalog</Typography>
                        </Grid2>
                    )}
                    <FormActions
                        onCancel={handleClose}
                        onSubmit={handleSave}
                        submitLabel="Sačuvaj"
                        disabled={
                            !isOrderDataValid || saveStatus === SaveEntityStatus.Pending || !isAvailableQuantityEnough
                        }
                        pending={saveStatus === SaveEntityStatus.Pending}
                    />
                </Grid2>
            </DialogContent>
        </ResponsiveDialog>
    );
}
